import { useEffect, useState, useCallback } from "react";
import { Clock } from "lucide-react";
import api from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

/**
 * AdminEtaConfig — admin-editable delivery-day estimates per zone + speed.
 *
 * Lives at /api/admin/eta-config (GET / PUT). Sprint 47.
 *
 * Design intent:
 *   • Tied to the existing 4 zones (A/B/C/D) defined in pricing_config —
 *     edit days here, NOT zone km cutoffs (those are in the Zones table).
 *   • "Express" uses the zone ETA as-is.
 *   • "Lazy" adds `days_offset` and floors at `min_days` — admin can
 *     redefine what "lazy" means without redeploying.
 *   • Existing orders are NOT affected. Their estimated_delivery_days is
 *     stamped at order creation. Only NEW quotes pick up the change.
 */
export default function AdminEtaConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/eta-config");
      setCfg(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ETA config");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateZoneDays = (zid, days) =>
    setCfg((c) => ({ ...c, zones: { ...c.zones, [zid]: { ...c.zones[zid], days } } }));

  const updateSpeed = (sid, key, value) =>
    setCfg((c) => ({ ...c, speeds: { ...c.speeds, [sid]: { ...c.speeds[sid], [key]: value } } }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        version: 1,
        zones: Object.fromEntries(
          Object.entries(cfg.zones).map(([zid, zc]) => [
            zid, { days: Number(zc.days) || 1, label: zc.label || zid },
          ])
        ),
        speeds: Object.fromEntries(
          Object.entries(cfg.speeds).map(([sid, sc]) => [
            sid, {
              days_offset: Number(sc.days_offset) || 0,
              min_days: Number(sc.min_days) || 1,
              label: sc.label || sid,
            },
          ])
        ),
      };
      const { data } = await api.put("/admin/eta-config", payload);
      setCfg(data);
      toast.success("ETA config saved — new quotes use the updated values");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail?.errors?.join("; ") || detail?.message || detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="eta-config-loading">
        <p className="text-sm text-slate-500">Loading ETA config…</p>
      </div>
    );
  }

  const zoneRows = Object.entries(cfg.zones).sort(([a], [b]) => a.localeCompare(b));
  const speedRows = Object.entries(cfg.speeds);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="eta-config-section">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
          <Clock className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Delivery ETA</h2>
          <p className="text-xs text-slate-500">
            Days shown on the customer price card. Existing orders keep their old ETA — only new quotes use these values.
          </p>
        </div>
      </div>

      {/* Per-zone days */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {zoneRows.map(([zid, zc]) => (
          <label
            key={zid}
            className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 border border-slate-100"
            data-testid={`eta-zone-row-${zid}`}
          >
            <div>
              <div className="text-sm font-medium text-slate-800">Zone {zid}</div>
              <div className="text-[11px] text-slate-500">{zc.label || ""}</div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={30}
                value={zc.days}
                onChange={(e) => updateZoneDays(zid, e.target.value)}
                className="w-20 h-9 text-sm"
                data-testid={`eta-zone-days-${zid}`}
              />
              <span className="text-xs text-slate-500">days</span>
            </div>
          </label>
        ))}
      </div>

      {/* Speed offsets */}
      <div className="mt-5">
        <div className="text-sm font-medium text-slate-800 mb-2">Speed options</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {speedRows.map(([sid, sc]) => (
            <div
              key={sid}
              className="bg-slate-50 rounded-xl px-3 py-3 border border-slate-100 space-y-2"
              data-testid={`eta-speed-row-${sid}`}
            >
              <div className="text-sm font-medium text-slate-800 capitalize">{sc.label || sid}</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-24">+ days offset</span>
                <Input
                  type="number"
                  min={0}
                  max={30}
                  value={sc.days_offset}
                  onChange={(e) => updateSpeed(sid, "days_offset", e.target.value)}
                  className="w-20 h-8 text-sm"
                  data-testid={`eta-speed-offset-${sid}`}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-24">min days</span>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={sc.min_days}
                  onChange={(e) => updateSpeed(sid, "min_days", e.target.value)}
                  className="w-20 h-8 text-sm"
                  data-testid={`eta-speed-min-${sid}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div className="text-[11px] text-slate-400">
          {cfg.updated_at ? `Last updated ${new Date(cfg.updated_at).toLocaleString()} by ${cfg.updated_by || "system"}` : ""}
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#FF6B00] hover:bg-[#E65A00] text-white"
          data-testid="eta-config-save"
        >
          {saving ? "Saving…" : "Save ETA Config"}
        </Button>
      </div>
    </div>
  );
}
